import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const MEDIA = process.env.REACT_APP_BACKEND_URL + "/api/files/noirbyamara/teaser";

const Block = ({ overline, title, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay }}
    className="max-w-2xl">
    <p className="overline text-gold mb-4">{overline}</p>
    <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight mb-6">{title}</h2>
    <div className="text-base md:text-lg text-espresso/75 leading-relaxed space-y-4">{children}</div>
  </motion.div>
);

const OriWorld = () => {
  return (
    <div data-testid="ori-world" className="bg-ivory text-espresso">
      <section className="relative h-[70vh] min-h-[460px] -mt-16 md:-mt-20 flex items-end overflow-hidden">
        <video autoPlay muted loop playsInline preload="metadata" poster={`${MEDIA}/ori_hero_poster.jpg`}
          className="absolute inset-0 w-full h-full object-cover bg-cocoa"
          style={{ backgroundImage: `url(${MEDIA}/ori_hero_poster.jpg)`, backgroundSize: "cover", backgroundPosition: "center" }}>
          <source src={`${MEDIA}/ori_teaser_muted.mp4`} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-cocoa/85 via-cocoa/30 to-cocoa/40" />
        <div className="relative max-w-[1600px] mx-auto w-full px-6 md:px-10 pb-14 text-cream">
          <p className="overline text-gold mb-4">The World of ORI</p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight">A name.<br />A lineage.<br />A way of dressing.</h1>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 space-y-24 md:space-y-32">
        <Block overline="The Name" title="What ORI means">
          <p>ORI speaks to origin — to the head, the source, the guiding self. It is the part of a person that carries intention and destiny. To wear ORI is to dress from that place: considered, rooted, quietly certain.</p>
          <p>Every piece is made to be lived in and layered into a life — not chased for a season, but kept.</p>
        </Block>

        <Block overline="The Cowrie" title="Shells that once held worth" delay={0.05}>
          <p>Across West Africa, the cowrie shell was currency, ornament and blessing — a small, luminous symbol of prosperity, protection and womanhood. We trim our pieces with cowrie as a thread back to that heritage.</p>
          <p>It is detail with meaning: a reminder that value is inherited, and beauty carries history.</p>
        </Block>

        <Block overline="The Craft" title="The chevron and the hand" delay={0.05}>
          <p>The chevron runs through our work — in knit, in weave, in the way a garment is built. It is rhythm and direction, the mark of something made with patience rather than haste.</p>
          <p>We favour texture you can feel and construction that holds: pieces designed to move with the body and soften with time.</p>
        </Block>

        <Block overline="The Intention" title="Heritage, worn now" delay={0.05}>
          <p>NOIRBYAMARAORI sits where lineage meets the present — contemporary silhouettes carrying the stories of where we come from. Not costume, not nostalgia. A modern wardrobe with memory.</p>
        </Block>

        <div className="pt-4">
          <Link to="/" data-testid="ori-world-cta"
            className="inline-block bg-espresso text-cream px-9 py-4 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-gold hover:text-cocoa transition-colors">
            Discover the collection
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OriWorld;
